import { ENVIRONMENT_DOMAINS, KNOWN_COUNTRY_CODE_TOP_LEVEL_DOMAINS, LOCAL_ENVS, } from '../constants/domain';
import { COUNTRIES_ISO_3, INTL_LOCALES } from '../constants/i18n';
import { EnvironmentTypes } from '../types';
/**
 * Checks if the current page is a client side rendered page.
 */
export const isCSR = () => typeof window !== 'undefined';
export const DOMAIN_LOCALE = {
    'weltsparen.de': 'de-DE',
    'weltsparen.at': 'de-AT',
    'raisin.co.uk': 'en-GB',
    'raisin.ie': 'en-IE',
    'raisin.es': 'es-ES',
    'raisin.fr': 'fr-FR',
    'raisin.nl': 'nl-NL',
    'raisin.com/pl-pl': 'pl-PL',
    'raisin.com/fi-fi': 'fi-FI',
    'raisin.com/fr-fr': 'fr-FR',
};
export const checkHost = (host) => { var _a; return isCSR() && ((_a = window.location.host) === null || _a === void 0 ? void 0 : _a.includes(host)); };
export const isLocalhost = () => checkHost('localhost');
export const isNetlify = () => checkHost('cp.raisin-netlify.com') || checkHost('--raisin-cp-zone-router.netlify.app');
export const getRegionFromHost = () => {
    var _a, _b, _c, _d, _e, _f, _g;
    if (process.env.NEXT_PUBLIC_REGION_NAME) {
        return process.env.NEXT_PUBLIC_REGION_NAME;
    }
    if (isCSR() && ((_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.pathname) === null || _b === void 0 ? void 0 : _b.toLowerCase().startsWith('/en-us/'))) {
        return COUNTRIES_ISO_3.USA;
    }
    if (isCSR() && isLocalhost()) {
        return ((_c = process.env.NEXT_PUBLIC_CP_REGION) === null || _c === void 0 ? void 0 : _c.toUpperCase()) || COUNTRIES_ISO_3.DEU;
    }
    return isCSR() &&
        (((_e = (_d = window === null || window === void 0 ? void 0 : window.location) === null || _d === void 0 ? void 0 : _d.hostname) === null || _e === void 0 ? void 0 : _e.endsWith('.co.uk')) ||
            checkHost('gbr-development') ||
            ((_g = (_f = window === null || window === void 0 ? void 0 : window.location) === null || _f === void 0 ? void 0 : _f.pathname) === null || _g === void 0 ? void 0 : _g.toLowerCase().startsWith(`/${INTL_LOCALES.UK.toLowerCase()}`)))
        ? COUNTRIES_ISO_3.GBR
        : COUNTRIES_ISO_3.DEU;
};
export const isGBRRegion = () => getRegionFromHost() === COUNTRIES_ISO_3.GBR;
export const getRegionFromLocale = (locale) => {
    switch (locale) {
        case 'en-GB':
            return 'GBR';
        case 'en-US':
            return COUNTRIES_ISO_3.USA;
        default:
            return 'DEU';
    }
};
export const isDEURegion = () => {
    var _a, _b, _c, _d;
    return isCSR() &&
        (((_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.hostname) === null || _b === void 0 ? void 0 : _b.endsWith('.de')) || ((_d = (_c = window === null || window === void 0 ? void 0 : window.location) === null || _c === void 0 ? void 0 : _c.hostname) === null || _d === void 0 ? void 0 : _d.endsWith('.at')));
};
export const isUSARegion = () => isCSR() && getRegionFromHost() === COUNTRIES_ISO_3.USA;
export const isCOM = () => isCSR() && window.location.hostname.endsWith('.com');
export const getNamespaceName = () => {
    if (isCSR()) {
        const name = /bk-(deu|gbr)-(.*)\.raisin-dev\.network/.exec(window.location.host);
        return name ? name[2] : EnvironmentTypes.DEVELOPMENT;
    }
    return EnvironmentTypes.DEVELOPMENT;
};
export const getEnvironment = () => {
    if (checkHost(EnvironmentTypes.STAGING))
        return EnvironmentTypes.STAGING;
    if (checkHost(EnvironmentTypes.ONBOARDING))
        return EnvironmentTypes.ONBOARDING;
    if (checkHost(EnvironmentTypes.PRODUCTION))
        return EnvironmentTypes.PRODUCTION;
    if (checkHost('dev-'))
        return EnvironmentTypes.DEVELOPMENT;
    // branch deploy pattern on Netlify
    if (isNetlify())
        return EnvironmentTypes.DEVELOPMENT;
    if (checkHost('raisin-dev.network'))
        return getNamespaceName();
    if (checkHost('localhost')) {
        return process.env.NEXT_PUBLIC_ENVIRONMENT_NAME || EnvironmentTypes.LOCAL;
    }
    return EnvironmentTypes.PRODUCTION;
};
export const isServerEnvironment = (value) => {
    if (!value || typeof value !== 'string') {
        return false;
    }
    return [
        EnvironmentTypes.DEVELOPMENT,
        EnvironmentTypes.STAGING,
        EnvironmentTypes.ONBOARDING,
        EnvironmentTypes.PRODUCTION,
    ].includes(value);
};
export const getServerEnvironment = () => {
    const envName = process.env.NEXT_PUBLIC_ENVIRONMENT_NAME || process.env.REACT_APP_ENV;
    if (!isServerEnvironment(envName)) {
        return undefined;
    }
    return envName;
};
export const isLocalEnv = LOCAL_ENVS.some((env) => isCSR() && env.test(window.location.host));
export const getEnvironmentFromAwsVars = (awsBranch, awsAppId) => {
    if (EnvironmentTypes[String(awsBranch).toUpperCase()]) {
        return awsBranch;
    }
    return awsAppId ? EnvironmentTypes.DEVELOPMENT : EnvironmentTypes.LOCAL;
};
export const isUnitedKingdomDomain = () => { var _a, _b; return isCSR() && ((_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.hostname) === null || _b === void 0 ? void 0 : _b.endsWith('.co.uk')); };
export const isRaisinComDomain = () => { var _a, _b; return (_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.hostname) === null || _b === void 0 ? void 0 : _b.endsWith('raisin.com'); };
export const isPolishDomain = () => window === null || window === void 0 ? void 0 : window.location.pathname.startsWith('/pl-pl');
export const getRaisinDomain = () => {
    var _a;
    const hostname = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.hostname;
    if (!hostname) {
        return 'raisin.com';
    }
    const parts = hostname.split('.');
    if (KNOWN_COUNTRY_CODE_TOP_LEVEL_DOMAINS.includes(parts.slice(-2).join('.'))) {
        return parts.slice(-3).join('.');
    }
    return parts.slice(-2).join('.');
};
export const getHostEnvironment = () => ENVIRONMENT_DOMAINS[getEnvironment().toLowerCase()];
export const getNormalizedHostname = () => {
    var _a, _b;
    const hostEnvironment = getHostEnvironment();
    return (_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.hostname) === null || _b === void 0 ? void 0 : _b.replace(`www.${hostEnvironment}`, '');
};
export const getLocalePathFromHost = () => {
    var _a, _b, _c, _d;
    if (isLocalhost()) {
        const appLocale = process.env.APP_LOCALE || process.env.NEXT_PUBLIC_APP_LOCALE;
        return appLocale === null || appLocale === void 0 ? void 0 : appLocale.toLowerCase();
    }
    //raisin.com and netlify domain
    if (isCOM()) {
        const locale = (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.pathname) === null || _b === void 0 ? void 0 : _b.split('/')) === null || _c === void 0 ? void 0 : _c[1];
        return locale === null || locale === void 0 ? void 0 : locale.toLowerCase();
    }
    let hostname = getNormalizedHostname();
    return (_d = DOMAIN_LOCALE === null || DOMAIN_LOCALE === void 0 ? void 0 : DOMAIN_LOCALE[hostname]) === null || _d === void 0 ? void 0 : _d.toLowerCase();
};
//# sourceMappingURL=domain.js.map